import { useEffect, useMemo } from "react";
import {
  AppointmentDto,
  ContinuousAppointmentDto,
  ContinuousEventTimeSlotDto,
  EventTimeSlotDto,
  LeaveOfAbsenceDto,
  PatientAvailabilityDto,
  SimpleWorkingHoursDto,
  SublocalityDto,
  TimeSlotDto,
  UserDto,
  WorkingHoursDto,
} from "../../../api-client";
import { mapSublocalityToColor } from "../../../helpers/generateColor";
import { mobiliTheme } from "../../../themes/mobiliTheme";
// import { Resource, PatientExt, TherapyExt, TherapyFrequencyExt } from "../Calendar.type";
import moment from "moment";
import {
  CalendarEvent,
  CalendarResource,
  ClientUser,
  adaptAppointmentsToCalendarEvents,
  adaptTimeSlotsToCalendarEvents,
} from "./adapters";
import {
  adaptContinuousAppointmentsToCalendarEvents,
  adaptContinuousTimeSlotsToCalendarEvents,
} from "./adapters/batch.utilities";
import { adaptUserToCalendarResource } from "./adapters/api-to-client/user.adapter";
import {
  adaptDerivedLunchBreakForContinuous,
  clientContinuousLunchBreakToCalendarEvent,
  adaptPublicHolidayFromApi,
  clientPublicHolidayToCalendarEvent,
} from "./adapters/api-to-client/specialized.adapter";
import { slotTherapy } from "../../../helpers/slotTherapy";

type CalendarEventsProps = {
  appointments: (AppointmentDto | ContinuousEventTimeSlotDto)[] | ContinuousAppointmentDto[];
  leaveOfAbsences: LeaveOfAbsenceDto[];
  eventTimeslots: EventTimeSlotDto[];
  continuousAppointments: ContinuousAppointmentDto[];
  continuousEventTimeSlots: ContinuousEventTimeSlotDto[];
  continuousLunchBreaks: WorkingHoursDto[];
  lunchBreaks: WorkingHoursDto[];
  publicHolidayWorkingHours?: WorkingHoursDto[];
  cancelledDays: WorkingHoursDto[];
  travelTimes: TimeSlotDto[];
  patientBlockingSlots: TimeSlotDto[];
  groupCheck?: boolean;
  lockedAppointments?: string[];
  isContinuous?: boolean;
  sublocalities?: SublocalityDto[];
};

export const toCalendarEvents = ({
  appointments,
  leaveOfAbsences,
  eventTimeslots,
  continuousAppointments,
  continuousEventTimeSlots,
  lunchBreaks,
  publicHolidayWorkingHours,
  continuousLunchBreaks,
  cancelledDays,
  travelTimes,
  patientBlockingSlots,
  groupCheck,
  lockedAppointments,
  isContinuous,
  sublocalities,
}: CalendarEventsProps): CalendarEvent[] => {
  // console.log("🔄 Generating events using new adapter system", isContinuous);

  // Transform appointments to calendar events
  const appointmentEvents = adaptAppointmentsToCalendarEvents(
    (appointments as AppointmentDto[]) || [],
    {
      groupCheck,
      lockedAppointments,
      sublocalities,
    }
  );

  // Transform continuous appointments to calendar events
  const continuousAppointmentEvents = adaptContinuousAppointmentsToCalendarEvents(
    (continuousAppointments as ContinuousAppointmentDto[]) || [],
    { groupCheck, lockedAppointments, sublocalities }
  );

  // console.log("📅 Generated continuous appointment events:", continuousAppointmentEvents);

  // Transform various timeslot types to calendar events
  const timeslotData = {
    leaveOfAbsences: leaveOfAbsences || [],
    eventTimeSlots: eventTimeslots || [],
    continuousEventTimeSlots: continuousEventTimeSlots || [],
    travelTimes: travelTimes || [],
    patientSlots: patientBlockingSlots || [],
    lunchBreaks: lunchBreaks || [],
  };

  const timeslotEvents = adaptTimeSlotsToCalendarEvents(timeslotData, { lockedAppointments });
  const continuousTimeslotEvents = adaptContinuousTimeSlotsToCalendarEvents(timeslotData, {
    lockedAppointments,
  });
  // console.log("📅 00000000000", continuousTimeslotEvents);

 
  // Lunch breaks (non-continuous) are handled inside adaptTimeSlotsToCalendarEvents
  // (with LoA-overlap filtering). Do NOT also produce them here, otherwise every
  // break is rendered twice (manifests as a "duplicate" after a drag, since the
  // moved exception break is emitted by both paths with the same id).
  const continuousLunchBreakEvents = continuousLunchBreaks
    .map((lb) => adaptDerivedLunchBreakForContinuous(lb))
    .map((ts) => clientContinuousLunchBreakToCalendarEvent(ts));

  const publicHolidayEvents = (publicHolidayWorkingHours || []).map((ph) =>
    clientPublicHolidayToCalendarEvent(adaptPublicHolidayFromApi(ph))
  );

  // cancelled days - render as background events to match non-working hours
  const cancelledDayEvents = (cancelledDays || []).map((cd): CalendarEvent => ({
    id: `cancelled-${cd.userId}-${cd.date}`,
    title: "Tag abgesagt",
    start: moment(cd.start).toISOString(),
    end: moment(cd.end).toISOString(),
    resourceId: cd.userId,
    editable: false,
    display: 'background',
    backgroundColor: 'rgba(0, 48, 89, 0.5)',
    classNames: ["cancelled-day"],
    extendedProps: {
      eventType: "working-hours",
      timeSlotType: "CancelledDay",
      user: { id: cd.userId } as ClientUser,
      isContinuous:false,
      isException: true,
      exceptionId: cd.exceptionId,
    },
  }));

  const allEvents = isContinuous
    ? [...continuousAppointmentEvents, ...continuousTimeslotEvents,...continuousLunchBreakEvents]
    : [...appointmentEvents, ...timeslotEvents, ...publicHolidayEvents, ...cancelledDayEvents];

  // console.log("📅 Generated events using adapters:", allEvents.length);

  return allEvents;
};

export const toCalendarResources = (
  users: UserDto[],
  options: {
    selectedDate: Date;
    isRouteplanView?: boolean;
    routeplanUser?: UserDto;
    selectedUser?: UserDto;
    patientAvailabilities?: PatientAvailabilityDto[];
    patientUnavailableTypes?: TimeSlotDto[];
    derivedWorkingHours?: WorkingHoursDto[];
    deltaMinutesMap?: Map<string, number>;
    yearDeltaMinutesMap?: Map<string, number>;
    warningCountMap?: Map<string, number>;
    continuousWorkPlansForUsers?: WorkingHoursDto[];
    isContinuous?: boolean;
  }
): CalendarResource[] => {
  const {
    selectedDate,
    isRouteplanView = false,
    routeplanUser,
    selectedUser,
    patientAvailabilities,
    patientUnavailableTypes,
    deltaMinutesMap,
    yearDeltaMinutesMap,
    warningCountMap,
    isContinuous = false,
  } = options;

  const selectedDayIndex = selectedDate.getDay();

  // Filter users based on the selected user, routeplan user, or show all
  const resourceUsers = routeplanUser ? [routeplanUser] : selectedUser ? [selectedUser] : users;
  // Map users to FullCalendar resources
  const resources: CalendarResource[] =
    resourceUsers?.map((user) => {
      const userWorkDays = isContinuous ? options.continuousWorkPlansForUsers?.filter((wh) => wh.userId === user.id && !wh.isBreak) || [] : options.derivedWorkingHours?.filter((wh) => wh.userId === user.id && !wh.isBreak) || [];
      const resourceTitle = isRouteplanView ? user.lastName! + " Route" : undefined;
      const deltaMinutes = deltaMinutesMap?.get(user.id!);
      const yearDeltaMinutes = yearDeltaMinutesMap?.get(user.id!);
      const warningCount = warningCountMap?.get(user.id!);
      return adaptUserToCalendarResource(user, userWorkDays, resourceTitle, deltaMinutes, warningCount, yearDeltaMinutes);
    }) || [];

  // Add Patient Availability resource if present
  // console.log("=== PATIENT AVAILABILITY CHECK ===");
  // console.log("patientAvailability:", patientAvailability);
  // console.log("patientUnavailableTypes:", patientUnavailableTypes);

  const selectedDateOnly = moment(selectedDate).format("YYYY-MM-DD");
  let shouldAddPatientAvailability =
    (patientAvailabilities && patientAvailabilities.length > 0 && 
      patientAvailabilities.some((pa) => 
        (!pa.validityStart || moment(pa.validityStart).format("YYYY-MM-DD") <= selectedDateOnly) &&
        (!pa.validityEnd || moment(pa.validityEnd).format("YYYY-MM-DD") >= selectedDateOnly)) ||
    (patientUnavailableTypes && patientUnavailableTypes.length > 0));

  if (shouldAddPatientAvailability) {
    // additional criteria that the availability must be relevant for any visible day
    // (not just selected day — supports weekly view showing all days)
    shouldAddPatientAvailability = 
      (patientAvailabilities?.some((pa) => pa.dayOfWeek != null) || false) ||
      (patientUnavailableTypes && patientUnavailableTypes.length > 0);
  }
  // console.log("shouldAddPatientAvailability:", patientAvailabilities, patientUnavailableTypes);

  if (shouldAddPatientAvailability) {
    // console.log("adding patient availability to resources", patientAvailabilities);

    const slotMinTime = "00:00:00";
    const slotMaxTime = "23:59:59";

    const businessHours: { daysOfWeek?: number[]; startTime: string; endTime: string }[] = [];

    // Collect all unique days from patient availability data
    const allDays = new Set<number>();
    patientAvailabilities?.forEach((pa) => {
      if (pa.dayOfWeek != null) allDays.add(pa.dayOfWeek);
    });

    // If no specific days found, fall back to selected day only
    if (allDays.size === 0) allDays.add(selectedDayIndex);

    // Process each day separately to support weekly view
    allDays.forEach((dayIndex) => {
      const patientUnavailabilityBlocks = patientAvailabilities?.filter(
        (pa) => pa.dayOfWeek === dayIndex
      ) || [];

      if (patientUnavailabilityBlocks.length > 0) {
        const hasTimeBlocks = patientUnavailabilityBlocks.some(
          (block) => block.startTime || block.endTime
        );

        if (!hasTimeBlocks) {
          // All blocks are for the whole day (no time specified) - patient is unavailable all day
          // Don't add any business hours for this day - the entire day will be non-business hours (blocked)
          // console.debug(`Found patient unavailability for the whole day ${dayIndex} (no available time)`);
          // Skip adding business hours - day will be fully blocked
        } else {
          // We have specific unavailability blocks, need to invert them to create business hours (available times)
          const sortedBlocks = [...patientUnavailabilityBlocks]
            .filter((block) => block.startTime && block.endTime)
            .sort((a, b) => {
              const timeA = moment(a.startTime).format("HH:mm");
              const timeB = moment(b.startTime).format("HH:mm");
              return timeA.localeCompare(timeB);
            });

          // console.debug(
            // `Found specific patient unavailability blocks for day ${dayIndex}:`,
            // sortedBlocks
          // );

          // Create business hours (available time) by inverting unavailability blocks
          let currentTime = slotMinTime;

          sortedBlocks.forEach((block) => {
            const blockStart = moment(block.startTime).format("HH:mm");
            const blockEnd = moment(block.endTime).format("HH:mm");

            // Add business hours from current time to the start of this unavailability block
            if (currentTime < blockStart) {
              businessHours.push({
                daysOfWeek: [dayIndex],
                startTime: currentTime,
                endTime: blockStart,
              });
            }

            // Move current time to the end of this unavailability block
            currentTime = blockEnd;
          });

          // Add remaining business hours from last block to end of day
          if (currentTime < slotMaxTime) {
            businessHours.push({
              daysOfWeek: [dayIndex],
              startTime: currentTime,
              endTime: slotMaxTime,
            });
          }
        }
      } else {
        // No unavailability blocks for this day - patient is available all day
        // console.debug(
          // `No patient unavailability found for day ${dayIndex}, patient is available all day.`
        // );
        businessHours.push({
          daysOfWeek: [dayIndex],
          startTime: slotMinTime,
          endTime: slotMaxTime,
        });
      }
    });

    // For weekdays (Mon=1 to Fri=5) not covered by unavailability data,
    // the patient is fully available — add full-day business hours so
    // those days don't appear blocked in the weekly view.
    for (let day = 1; day <= 5; day++) {
      if (!allDays.has(day)) {
        businessHours.push({
          daysOfWeek: [day],
          startTime: slotMinTime,
          endTime: slotMaxTime,
        });
      }
    }

    const patientResource: CalendarResource = {
      id: "0", // id 0 is reserved for patient availability
      title: "PA",
      businessHours,
      extendedProps: {},
    };

    resources.push(patientResource);
  } else {
    // console.log("NOT adding patient availability resource");
  }

  // Add old routeplan resource for comparison in route planning mode
  if (isRouteplanView && routeplanUser) {
    const routeplanResource: CalendarResource = {
      id: "1", // id 1 is reserved for old routeplan
      title: routeplanUser.lastName! + " Route (old)",
      businessHours: resources.find((r) => r.id === String(routeplanUser.id))?.businessHours,
      extendedProps: {},
    };
    resources.push(routeplanResource);
  }

  // console.log("=== FINAL RESOURCES ===");
  // console.log("final resources count:", resources.length);
  // console.log("final resources:", resources.map(r => ({ id: r.id, title: r.title })));

  return resources;
};

// export const useEffectOld = (
//   routeplanUser: UserDto,
//   selectedUser: UserDto,
//   users: any,
//   selectedDate: Date,
//   selectedDayIndex: number,
//   resourceUsers: any,
//   isRouteplanView: boolean,
//   patientAvailabilities: any,
//   patientUnavailableTypes: any
// ) => {
//   useEffect(() => {
//     // RESOURCES //////////////////////////////////////////////
//     const resourceUsers = routeplanUser ? [routeplanUser] : selectedUser ? [selectedUser] : users;
//     const resources = resourceUsers?.map((user: any, index: any) => {
//       //   const selectedDate = getSelectedDate();
//       //   const selectedDayIndex: number = getSelectedDate().getDay();

//       const validWeeklyWorkDays = user.weeklyWorkDays?.filter((f: any) => {
//         const validityStart = f.workPlan?.validityStart ? new Date(f.workPlan.validityStart) : null;
//         const validityEnd = f.workPlan?.validityEnd ? new Date(f.workPlan.validityEnd) : null;

//         return (
//           f.dayOfWeek === selectedDayIndex &&
//           (!validityStart || validityStart <= selectedDate) &&
//           (!validityEnd || validityEnd >= selectedDate)
//         );
//       });

//       const businessHours =
//         validWeeklyWorkDays && validWeeklyWorkDays.length > 0
//           ? validWeeklyWorkDays.map((day: any) => {
//               return {
//                 daysOfWeek: [day.dayOfWeek],
//                 startTime: moment(day.startTime).format("HH:mm"),
//                 endTime: moment(day.endTime).format("HH:mm"),
//               };
//             })
//           : [
//               {
//                 daysOfWeek: [selectedDayIndex],
//                 startTime: "00:00",
//                 endTime: "00:00",
//               },
//             ];

//       // console.log("businessHours", businessHours);

//       let resource: Resource = {
//         id: user.id!,
//         title: isRouteplanView ? user.lastName! + " Route" : user.nickName!,
//         // eventColor: generateColor(user.nickName!),
//         businessHours: businessHours,
//         extendedProps: {
//           link: user.id,
//         },
//       };
//       return resource;
//     });

//     // toggleSlotLaneVisibility(false); // TODO implement

//     // add new resource for Patient Availability
//     const patientAvailability = patientAvailabilities?.find(
//       (pa:any) => pa.dayOfWeek === selectedDate.getDay()
//     );
//     if (patientAvailability || (patientUnavailableTypes && patientUnavailableTypes.length > 0)) {
//       console.log("adding patient availability to resources", patientAvailability);
//     //   setPatientAvailability(patientAvailability); // TODO reimplement
//     const slotMinTime = "00:00:00";
//     const slotMaxTime = "23:59:59";
//       const newResource: Resource = {
//         id: "0", // id 0 is reserved for patient availability
//         title: "PA",
//         businessHours: [
//           {
//             daysOfWeek: [patientAvailability?.dayOfWeek],
//             startTime: patientAvailability?.startTime
//               ? moment(patientAvailability?.startTime).format("HH:mm")
//               : slotMinTime,
//             endTime: patientAvailability?.endTime
//               ? moment(patientAvailability?.endTime).format("HH:mm")
//               : slotMaxTime,
//           },
//         ],
//       };
//       resources?.push(newResource);
//       console.log("newResource for patient availability", newResource);

//     //   toggleSlotLaneVisibility(true); // TODO reimplement
//     }

//     // add a copy of routeplanUser's resource for showing the old routeplan
//     if (isRouteplanView && routeplanUser) {
//       const routeplanResource: Resource = {
//         id: "1", // id 1 is reserved for old routeplan
//         title: routeplanUser.lastName! + " Route (old)",
//         businessHours: resources?.find((r) => r.id === routeplanUser.id)?.businessHours,
//       };
//       resources?.push(routeplanResource);
//     }

//     // setResources(resources!); // TODO reimplement

//     // EVENTS //////////////////////////////////////////////
//     const events: Event[] = [];

//     // continuous appointments as events
//     if (isContinuous) {
//       const continuous = continuousAppointments
//         ?.map((appointment: ContinuousAppointmentDto) => {
//           let patient = appointment.timeSlots![0].therapyRx?.rx?.patient as PatientExt;
//           let multipleTimeSlots = appointment.timeSlots!.length > 1;
//           let evts = appointment.timeSlots?.map((timeSlot) => {
//             const therapy = slotTherapy(timeSlot) as TherapyExt;
//             const locked = lockedAppointments.includes(appointment.id?.toString()!);
//             const coordinate = sublocalities?.find(
//               (sub) => sub.addressId === appointment.address?.id
//             );
//             let event: Event = {
//               id: timeSlot.id?.toString(), // should be a unique id
//               title: patient.firstName?.[0] + "." + patient.lastName,
//               start: timeSlot.start!,
//               end: timeSlot.end!,
//               patient: patient,
//               // startTime, endTime and daysOfWeek are used for recurring events
//               startTime: moment(timeSlot.start!).format("HH:mm:ss"),
//               endTime: moment(timeSlot.end!).format("HH:mm:ss"),
//               daysOfWeek: [moment(timeSlot.start!).day().toString()],
//               resourceId: timeSlot.user?.id,
//               groupId: appointment.id?.toString(),
//               appointmentId: appointment.id?.toString(),
//               type: "TimeSlot",
//               multipleTimeSlots,
//               timeSlot: {
//                 id: timeSlot.id,
//                 patient: patient,
//                 therapy: therapy,
//                 user: timeSlot.user?.lastName!,
//                 visitType: appointment.address?.type,
//                 visitFee: timeSlot?.visitFee?.abbreviation,
//                 therapyRx: timeSlot.therapyRx,
//                 appointmentId: appointment.id?.toString(),
//               },
//               frequency: appointment.frequency as TherapyFrequencyExt,
//               color: coordinate?.sublocality
//                 ? mapSublocalityToColor(coordinate?.sublocality) // If no sublocality, use a therapy-specific color
//                 : "default", //mapTherapyToColor(therapy.abbreviation)
//               display: heatTreatmentBackground(therapy),
//               containsHeatTreatment: heatTreaments.includes(therapy.abbreviation!),
//               address: appointment.address,
//               editable: !locked,
//               resizable: !locked,
//               sublocality: sublocalities?.find((sub) => sub.addressId === appointment.address?.id)
//                 ?.sublocality,
//             };
//             return event;
//           });

//           // group contains heat treatments, note all other events
//           if (evts?.some((event) => event.display === "background")) {
//             console.log("grouping heat treatments");
//             evts?.forEach((event) => {
//               if (event.display !== "background") event.containsHeatTreatment = true;
//             });
//           }

//           return evts!;
//         })
//         .flat();

//       if (continuous) events.push(...continuous);

//       const continuousEventTimeSlot = continuousEventTimeSlots?.map((eventTimeslot, index) => {
//         let ets: Event = {
//           id: eventTimeslot.id?.toString(),
//           title: eventTimeslot.title!,
//           start: eventTimeslot.start!,
//           end: eventTimeslot.end!,
//           startTime: moment(eventTimeslot.start!).format("HH:mm:ss"),
//           endTime: moment(eventTimeslot.end!).format("HH:mm:ss"),
//           daysOfWeek: [moment(eventTimeslot.start!).day().toString()],
//           resourceId: eventTimeslot.user?.id,
//           type: "ContinuousEventTimeSlot",
//           editable: true,
//           resourceEditable: true,
//           resizable: true,
//           color: mobiliTheme.palette.secondary.main,
//         };
//         return ets;
//       });
//       events.push(...continuousEventTimeSlot!);

//       if (patientAvailability && patientAvailability.startTime && patientAvailability.endTime) {
//         const paEvent: Event = {
//           id: "0", // id 0 is reserved for patient availability
//           title: patientAvailability.patient?.lastName!,
//           start: patientAvailability.startTime,
//           end: patientAvailability.endTime,
//           startTime: moment(patientAvailability.startTime!).format("HH:mm:ss"),
//           endTime: moment(patientAvailability.endTime!).format("HH:mm:ss"),
//           daysOfWeek: [patientAvailability.dayOfWeek?.toString()!],
//           resourceId: "0",
//           type: "PatientAvailability",
//           color: mobiliTheme.palette.info.main,
//           resourceEditable: false,
//           editable: false,
//           classNames: ["striped-event"],
//         };
//         events.push(paEvent);
//       }
//     } else {
//       // console.log("processing appointments into events");
//       // timeslots as events
//       const timeslots = appointments
//         ?.map((appointment) => {
//           let patient = appointment.timeSlots![0].therapyRx?.rx?.patient as PatientExt | undefined;
//           let multipleTimeSlots = appointment.timeSlots!.length > 1;
//           let evts = appointment.timeSlots?.map((timeSlot) => {
//             const therapy = slotTherapy(timeSlot) as TherapyExt;
//             const locked = lockedAppointments.includes(appointment.id?.toString()!);
//             const coordinate = sublocalities?.find(
//               (sub) => sub.addressId === appointment.address?.id
//             );
//             let event: Event = {
//               id: timeSlot.id?.toString(), // should be a unique id
//               title: patient?.firstName?.[0] + "." + patient?.lastName,
//               start: timeSlot.start!,
//               end: timeSlot.end!,
//               resourceId: timeSlot.user?.id,
//               groupId: groupCheck ? appointment.id?.toString() + timeSlot.user?.id! : "",
//               appointmentId: appointment.id?.toString(),
//               multipleTimeSlots,
//               patient: patient,
//               address: appointment.address,
//               timeSlot: {
//                 id: timeSlot.id,
//                 patient: patient,
//                 therapy: therapy,
//                 user: timeSlot.user?.lastName!,
//                 visitType: appointment.address?.type,
//                 visitFee: timeSlot?.visitFee?.abbreviation,
//                 type: timeSlot.type,
//                 therapyRx: timeSlot.therapyRx,
//                 appointmentId: appointment.id?.toString(),
//                 treatmentIndex: timeSlot.treatmentIndex,
//                 isCancelled: appointment.originalDate && !appointment.resolved ? true : false,
//                 reason: appointment.reason ? appointment.reason : "",
//               },
//               type: "TimeSlot",
//               frequency: appointment.frequency as TherapyFrequencyExt,
//               color: appointment.attended
//                 ? mobiliTheme.palette.success.main // If attended, use the success color
//                 : coordinate?.sublocality
//                 ? mapSublocalityToColor(coordinate?.sublocality) // If no sublocality, use a therapy-specific color
//                 : "default", // mapTherapyToColor(therapy.abbreviation) // Otherwise, use the therapy-specific color
//               // classNames: appointment.attended?["striped-event"]:[],
//               classNames: [
//                 appointment.attended || timeSlot.attended ? "striped-event" : "", // Apply "striped-event" if attended
//                 appointment.originalDate && !appointment.resolved && !appointment.end
//                   ? "striped-cancelled-event" // Apply "striped-cancelled-event" if cancelled
//                   : "",
//               ].filter(Boolean),

//               attended: appointment.attended,
//               editable:
//                 isRouteplanView && appointment.address?.type === "Praxis"
//                   ? false
//                   : !appointment.attended && !locked,
//               resizable: !locked,
//               resourceEditable: !appointment.attended,
//               display: heatTreatmentBackground(therapy),
//               hasNotes: appointment.notes?.length! > 0,
//               containsHeatTreatment: heatTreaments.includes(therapy.abbreviation!),
//               sublocality: coordinate?.sublocality,
//             };
//             // console.debug("event created:", event);
//             return event;
//           });

//           // group contains heat treatments, note all other events
//           if (evts?.some((event) => event.display === "background")) {
//             console.log("grouping heat treatments");
//             evts?.forEach((event) => {
//               if (event.display !== "background") event.containsHeatTreatment = true;
//             });
//           }

//           return evts!;
//         })
//         .flat();

//       // add new event for Patient Availability from patient_availability table
//       if (patientAvailability) {
//         const paEvent: Event = {
//           id: "0", // id 0 is reserved for patient availability
//           title:
//             patientAvailability.patient?.firstName + " " + patientAvailability.patient?.lastName!,
//           start: patientAvailability.startTime ? patientAvailability.startTime : slotMinTime,
//           end: patientAvailability.endTime ? patientAvailability.endTime : slotMaxTime,
//           startTime: patientAvailability.startTime
//             ? moment(patientAvailability.startTime).format("HH:mm:ss")
//             : slotMinTime,
//           endTime: patientAvailability.endTime
//             ? moment(patientAvailability.endTime).format("HH:mm:ss")
//             : slotMaxTime,
//           daysOfWeek: [patientAvailability.dayOfWeek?.toString()!],
//           resourceId: "0",
//           type: "PatientAvailability",
//           color: mobiliTheme.palette.info.main,
//           resourceEditable: false,
//           editable: false,
//           classNames: ["striped-event"],
//         };
//         timeslots?.push(paEvent);
//       }

//       // add PatientUnavailable types from time_slots table
//       if (patientUnavailableTypes) {
//         const patientUnavailableEvents = patientUnavailableTypes.map(
//           (patientUnavailable, index) => {
//             let pu: Event = {
//               id: patientUnavailable.id?.toString(),
//               title:
//                 patientUnavailable.patient?.firstName + " " + patientUnavailable.patient?.lastName,
//               start: patientUnavailable.start!,
//               end: patientUnavailable.end!,
//               resourceId: "0",
//               type: "PatientUnavailable",
//               editable: true,
//               resourceEditable: false,
//               resizable: true,
//               color: mobiliTheme.palette.error.main,
//               classNames: ["striped-event"],
//             };
//             return pu;
//           }
//         );
//         timeslots?.push(...patientUnavailableEvents);
//       }

//       events.push(...timeslots!);

//       // goneFishings as events
//       const goneFishingsEvents = goneFishings?.map((goneFishing, index) => {
//         let gfs: Event = {
//           id: goneFishing.id?.toString(),
//           title: goneFishing.user?.lastName!,
//           start: goneFishing.start!,
//           end: goneFishing.end!,
//           resourceId: goneFishing?.user?.id,
//           color: "black",
//           type: "GoneFishing",
//           editable: false,
//           resizable: false,
//           resourceEditable: false,
//         };
//         return gfs;
//       });
//       events.push(...goneFishingsEvents!);

//       const leaveOfAbsencesEvents = leaveOfAbsences?.map((leaveOfAbsence, index) => {
//         let loa: Event = {
//           id: leaveOfAbsence.id?.toString(),
//           title: leaveOfAbsence.loAType!,
//           start: moment(leaveOfAbsence.start!).toISOString(),
//           end: moment(leaveOfAbsence.end!).toISOString(),
//           resourceId: leaveOfAbsence.user?.id,
//           type: "LeaveOfAbsence",
//           editable: true,
//           resourceEditable: false,
//           resizable: true,
//           classNames: ["striped-event"],
//           color: mobiliTheme.palette.info.main,
//         };
//         return loa;
//       });
//       events.push(...leaveOfAbsencesEvents!);

//       const eventTimeslotsEvents = eventTimeslots?.map((eventTimeslot, index) => {
//         const locked = lockedAppointments.includes(eventTimeslot.id?.toString()!);
//         let ets: Event = {
//           id: eventTimeslot.id?.toString(),
//           title: eventTimeslot.title!,
//           start: eventTimeslot.start!,
//           end: eventTimeslot.end!,
//           resourceId: eventTimeslot.user?.id,
//           type: "EventTimeSlot",
//           editable: !locked,
//           resourceEditable: true,
//           resizable: !locked,
//           color: mobiliTheme.palette.secondary.main,
//         };
//         return ets;
//       });
//       events.push(...eventTimeslotsEvents!);
//       const lunchBreakEvents = lunchBreaks?.map((lunchBreak, index) => {
//         let ets: Event = {
//           id: lunchBreak.id?.toString(),
//           title: "Pause",
//           start: lunchBreak.start!,
//           end: lunchBreak.end!,
//           resourceId: lunchBreak.user?.id,
//           type: "LunchBreak",
//           editable: true,
//           resourceEditable: true,
//           resizable: true,
//           color: mobiliTheme.palette.info.light,
//         };
//         return ets;
//       });
//       events.push(...lunchBreakEvents!);

//       const travelTimeEvents = travelTimes?.map((travelTime, index) => {
//         let ets: Event = {
//           id: travelTime.id?.toString(),
//           title: "Fahrzeit",
//           start: travelTime.start!,
//           end: travelTime.end!,
//           resourceId: travelTime.user?.id,
//           type: "TravelTime",
//           editable: true,
//           resourceEditable: true,
//           resizable: true,
//           color: mobiliTheme.palette.info.dark,
//         };
//         return ets;
//       });
//       events.push(...travelTimeEvents!);

//       const simpleWorkingHoursEvents = simpleWorkingHours?.map((simpleWorkingHour, index) => {
//         let swh: Event = {
//           id: simpleWorkingHour.id?.toString(),
//           title: "SimpleWorkingHours",
//           start: simpleWorkingHour.start!,
//           end: simpleWorkingHour.end!,
//           resourceId: simpleWorkingHour.user?.id,
//           type: "SimpleWorkingHours",
//           editable: true,
//           resourceEditable: true,
//           resizable: true,
//           color: mobiliTheme.palette.info.main,
//         };
//         return swh;
//       });
//       events.push(...simpleWorkingHoursEvents!);
//     }

//     if (isRouteplanView) {
//       // TODO: Refactor route planning to use new adapter system
//       console.warn("Route planning view needs to be refactored for new adapter system");
//       /*
//         try {
//           // add old routeplan events to the events
//           const routeplanUserId = routeplanUser?.id;
//           const routeplanUserEvents = events?.filter((e) => e.resourceId === routeplanUserId);
//           const oldRouteplanEvents = routeplanUserEvents?.map((event) => {
//             let oldEvent: Event = {
//               ...event,
//               title: event.title + " (old)",
//               id: event.id + "_old",
//               resourceId: "1",
//               groupId: event.groupId + "_old",
//               editable: false,
//               resizable: false,
//               resourceEditable: false,
//             };
//             return oldEvent;
//           });
//           events.push(...oldRouteplanEvents!);

//           // filter out user appointments without addresses
//           if (googleMapAppointments) {
//             console.log("appointmentsWithValidAddresses", googleMapAppointments);
//             setShowLoading(true);

//             // get route durations for appointments
//             getRouteDurationsForAppointments(googleMapAppointments)
//               .then((routeDurations: (number | null)[]) => {
//                 setRouteDurations(routeDurations);
//                 console.log("routeDuration", routeDurations);

//                 // create travel time events for route durations
//                 const travelTimeslots: TimeSlotDto[] = [];
//                 routeDurations
//                   .filter((duration) => duration !== null)
//                   .forEach((duration, index: number) => {
//                     let appointmentLastTimeSlot =
//                       googleMapAppointments[index].timeSlots![
//                         googleMapAppointments[index].timeSlots!.length - 1
//                       ];
//                     let timeSlot: TimeSlotDto = {
//                       start: appointmentLastTimeSlot.end!,
//                       end: moment(appointmentLastTimeSlot.end!)
//                         .add(duration, "minutes")
//                         .toISOString(),
//                       type: "TravelTime",
//                       user: routeplanUser,
//                     };
//                     travelTimeslots.push(timeSlot);

//                     // adjust the next appointment's start time to follow the travel time duration
//                     if (googleMapAppointments[index + 1]) {
//                       let nextAppointment = googleMapAppointments[index + 1];
//                       let nextAppointmentFirstTimeSlot = nextAppointment.timeSlots![0];

//                       // calculate the duration of the next appointment
//                       const nextAppointmentStart = moment(nextAppointmentFirstTimeSlot.start);
//                       const nextAppointmentEnd = moment(nextAppointmentFirstTimeSlot.end);
//                       const nextAppointmentDuration = nextAppointmentEnd.diff(
//                         nextAppointmentStart,
//                         "minutes"
//                       );

//                       // set the next appointment's start time to the end of the travel time
//                       nextAppointmentFirstTimeSlot.start = timeSlot.end;

//                       // adjust the next appointment's end time based on its duration
//                       nextAppointmentFirstTimeSlot.end = moment(timeSlot.end)
//                         .add(nextAppointmentDuration, "minutes")
//                         .toISOString();
//                     }
//                   });
//                 setTravelTimeSlots(travelTimeslots);

//                 const travelTimeEvents: Event[] = travelTimeslots.map((ts, index) => {
//                   let event: Event = {
//                     id: "travelTime_" + index,
//                     title: "Fahrzeit",
//                     start: ts.start!,
//                     end: ts.end!,
//                     resourceId: routeplanUserId,
//                     type: "NewTravelTime",
//                     resourceEditable: false,
//                     resizable: false,
//                     color: mobiliTheme.palette.secondary.main,
//                   };
//                   return event;
//                 });
//                 events.push(...travelTimeEvents);
//                 setEvents([...events, ...travelTimeEvents]);

//                 // timeslots as events
//                 const timeslots = googleMapAppointments
//                   ?.map((appointment) => {
//                     let patient = appointment.timeSlots![0].therapyRx?.rx?.patient as PatientExt;
//                     let multipleTimeSlots = appointment.timeSlots!.length > 1;
//                     const locked = lockedAppointments.includes(appointment.id?.toString()!);
//                     let evts = appointment.timeSlots?.map((timeSlot) => {
//                       const therapy = slotTherapy(timeSlot) as TherapyExt;
//                       let event: Event = {
//                         id: timeSlot.id?.toString(), // should be a unique id
//                         title: patient.lastName,
//                         start: timeSlot.start!,
//                         end: timeSlot.end!,
//                         resourceId: timeSlot.user?.id,
//                         groupId: groupCheck ? appointment.id?.toString() + timeSlot.user?.id! : "",
//                         appointmentId: appointment.id?.toString(),
//                         type: "TimeSlot",
//                         multipleTimeSlots,
//                         patient: patient,
//                         address: appointment.address,
//                         timeSlot: {
//                           id: timeSlot.id,
//                           patient: patient,
//                           therapy: therapy,
//                           user: timeSlot.user?.lastName!,
//                           visitType: appointment.address?.type,
//                           visitFee: timeSlot?.visitFee?.abbreviation,
//                           therapyRx: timeSlot.therapyRx,
//                           appointmentId: appointment.id?.toString(),
//                           treatmentIndex: timeSlot.treatmentIndex,
//                           isCancelled:
//                             appointment.originalDate && !appointment.resolved ? true : false,
//                         },
//                         frequency: appointment.frequency as TherapyFrequencyExt,
//                         color: appointment.attended
//                           ? mobiliTheme.palette.success.main // If attended, use the success color
//                           : mapTherapyToColor(therapy.abbreviation), // Otherwise, use the therapy-specific color
//                         // classNames: appointment.attended?["striped-event"]:[],
//                         classNames: [
//                           appointment.attended ? "striped-event" : "", // Apply "striped-event" if attended
//                           appointment.originalDate && !appointment.resolved && !appointment.end
//                             ? "striped-cancelled-event" // Apply "striped-cancelled-event" if cancelled
//                             : "",
//                         ].filter(Boolean),
//                         attended: appointment.attended,
//                         editable:
//                           isRouteplanView && appointment.address?.type === "Praxis"
//                             ? false
//                             : !appointment.attended && !locked,
//                         resizable: !locked,
//                         resourceEditable: !appointment.attended,
//                         display: heatTreatmentBackground(therapy),
//                         hasNotes: appointment.notes?.length! > 0,
//                         containsHeatTreatment: heatTreaments.includes(therapy.abbreviation!),
//                       };
//                       return event;
//                     });
//                     return evts!;
//                   })
//                   .flat();
//                 events.push(...timeslots);
//                 // const newTravelTimesEvents = events.filter(event => !(event.type === "TravelTime" && event.resourceId !== "1"));
//                 // setEvents(events!); // TODO: Update for new adapter system
//               })
//               .catch((error) => {
//                 console.error("Error getting route durations", error);
//               })
//               .finally(() => {
//                 setShowLoading(false);
//               });
//           }
//         } catch (error) {
//           console.error("Error during routeplan view:", error);
//         }
//         */
//     }

//     // setEvents(events!); // TODO: Update for new adapter system
//     console.log("calendarEvents", calendarEvents);
//   }, [
//     appointments,
//     continuousAppointments,
//     goneFishings,
//     leaveOfAbsences,
//     eventTimeslots,
//     continuousEventTimeSlots,
//     users,
//     groupCheck,
//     routeplanUser,
//     googleMapAppointments,
//   ]);
// };
